import { Fragment, useCallback, useEffect, useRef, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Add as AddIcon } from '@factorialco/f0-react/icons/app'

import { BuildingBlock } from 'modules/workflows/components/WorkflowBuildingBlocksBox'
import { useWorkflowContext } from 'modules/workflows/contexts/WorkflowContext'
import useClickOutside from 'modules/workflows/hooks/useClickOutside'

import { NodeUIType } from './types'
import { createNodesAndEdges } from './utils'

type Props = { nodeId: string; nodeType: NodeUIType; isOpen?: boolean }

export const FlowNodeAddButton = ({ nodeId, nodeType, isOpen = false }: Props) => {
  const [open, setOpen] = useState(isOpen)
  const [isHoveringItem, setIsHovertingItem] = useState<boolean>(false)
  const [hoveringBuildingBlock, setHoveringBuildingBlock] = useState<BuildingBlock | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const {
    buildingBlocks,
    nodesUI,
    setNodesUI,
    edgesUI,
    setEdgesUI,
    elevateNodeZIndex,
    restoreNodeZIndex,
    menuControls,
  } = useWorkflowContext()

  const closeMenu = useCallback(() => {
    setOpen(false)
    restoreNodeZIndex(nodeId)
  }, [restoreNodeZIndex, nodeId])

  // Register this menu with the workflow context when it opens
  useEffect(() => {
    if (open) {
      return menuControls.registerMenu(nodeId, closeMenu)
    }
  }, [open, nodeId, menuControls, closeMenu])

  useClickOutside(menuRef, () => {
    menuControls.closeAllMenus()
  })

  const handleAddIconClick = (event: React.MouseEvent | MouseEvent) => {
    event.stopPropagation()
    menuControls.closeAllMenus()
    elevateNodeZIndex(nodeId)
    setOpen(true)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      event.stopPropagation()
      menuControls.closeAllMenus()
      elevateNodeZIndex(nodeId)
      setOpen(true)
    }
  }
  const handleNodeChosenClick = (type: NodeUIType) => {
    const currentNodes = nodesUI
    const currentEdges = edgesUI
    const newNodeId = crypto.randomUUID()

    // Find the current node to get its position
    const currentNode = currentNodes.find((node) => node.id === nodeId)
    if (!currentNode) return

    // Create the new node(s) and edges
    const { nodes: newNodes, edges: newEdges = [] } = createNodesAndEdges(newNodeId, type, {
      x: currentNode.position.x,
      y: currentNode.position.y,
    })

    // For condition nodes, connect all nodes under the current one to the else node
    let updatedEdges = currentEdges
    let connectionEdge

    if (type === 'condition') {
      // Find the else node (it should be the last node in the array for condition type)
      const elseNode = newNodes.find((node) => node.type === 'conditionElse')
      if (elseNode) {
        // Update outgoing edges to point to the else node instead
        updatedEdges = currentEdges.map((edge) => {
          if (edge.source === nodeId) {
            return {
              ...edge,
              source: elseNode.id,
            }
          }
          return edge
        })

        // Create new edge from current node to the main condition node
        connectionEdge = {
          id: `${nodeId}-${newNodeId}`,
          source: nodeId,
          target: newNodeId,
          type: 'default' as const,
        }
      }
    } else {
      // For single nodes, use the existing logic
      const newNode = newNodes[0]
      if (!newNode) return

      // Update outgoing edges to point to the new node instead
      updatedEdges = currentEdges.map((edge) => {
        if (edge.source === nodeId) {
          return {
            ...edge,
            source: newNode.id,
          }
        }
        return edge
      })

      // Create new edge from current node to new node
      connectionEdge = {
        id: `${nodeId}-${newNode.id}`,
        source: nodeId,
        target: newNode.id,
        type: 'default' as const,
      }
    }

    // Add all new nodes and update edges
    // Important: Set nodes FIRST, then edges, so that when the edges change triggers
    // validation recalculation, the new nodes are already in state with suppressInitialValidation
    const finalEdges = connectionEdge
      ? [...updatedEdges, ...newEdges, connectionEdge]
      : [...updatedEdges, ...newEdges]
    setNodesUI([...currentNodes, ...newNodes])
    setEdgesUI(finalEdges)

    // Close the menu
    closeMenu()
  }

  if (nodeType === 'condition' || nodeType === 'terminate') return

  return (
    <Box
      position="fixed"
      width="full"
      style={{ left: '0', bottom: '-35px' }}
      flexDirection="column"
      alignItems="center"
    >
      <Box
        position="relative"
        width="full"
        flexDirection="column"
        alignItems="center"
        ref={menuRef}
      >
        <F0Button
          icon={AddIcon}
          hideLabel
          variant="outline"
          size="sm"
          onClick={handleAddIconClick}
          onKeyDown={handleKeyDown}
          aria-label="Add node"
          aria-expanded={open}
          aria-haspopup="menu"
          aria-controls={open ? `building-blocks-menu-${nodeId}` : undefined}
        />

        <Box
          id={`building-blocks-menu-${nodeId}`}
          borderRadius={{ all: 'abs012' }}
          background="white"
          flexDirection="column"
          alignItems="flexStart"
          justifyContent="center"
          position="absolute"
          border={{ all: { color: 'grey400', style: 'solid', width: 's1' } }}
          paddingY="s4"
          paddingX="s4"
          role="menu"
          aria-label="Building blocks"
          style={{
            visibility: open ? 'visible' : 'hidden',
            width: '160px',
            transition: 'all 150ms ease-in-out',
            opacity: open ? '1' : '0',
            zIndex: '99',
            left: '56%',
            top: open ? '15px' : '0',
          }}
        >
          {buildingBlocks.map((block) => (
            <Fragment key={block.key}>
              <Box
                onMouseEnter={() => {
                  setHoveringBuildingBlock(block)
                  setIsHovertingItem(true)
                }}
                onMouseLeave={() => {
                  setIsHovertingItem(false)
                }}
                cursor={'pointer'}
                width="full"
                paddingY="s4"
                paddingX="s6"
                flexDirection="row"
                alignItems="center"
                gap="s12"
                background={
                  isHoveringItem && hoveringBuildingBlock?.key === block.key ? 'grey200' : undefined
                }
                borderRadius={{ all: 'abs008' }}
                style={{ transition: 'background-color 120ms ease-in-out' }}
                onClick={(event: React.MouseEvent | MouseEvent) => {
                  event.stopPropagation()
                  handleNodeChosenClick(block.nodeType)
                }}
                role="menuitem"
              >
                <Box
                  flexDirection="row"
                  style={{ backgroundColor: block.background }}
                  borderRadius={{ all: 'abs006' }}
                  width="s24"
                  height="s24"
                  alignItems="center"
                  justifyContent="center"
                >
                  <block.icon
                    width={18}
                    height={18}
                    style={{ color: block.color, strokeWidth: 1 }}
                    aria-hidden="true"
                  />
                </Box>
                <Text size="100" weight="medium" color="textPrimary">
                  {block.title}
                </Text>
              </Box>
            </Fragment>
          ))}
          <Box
            borderRadius={{ all: 'abs012' }}
            className="bg-f1-background-inverse"
            flexDirection="column"
            alignItems="flexStart"
            justifyContent="center"
            position="absolute"
            paddingY="s12"
            paddingX="s12"
            gap="s4"
            style={{
              width: '260px',
              transition: 'all 150ms ease-in-out',
              opacity: isHoveringItem ? '1' : '0',
              zIndex: '99',
              left: 'calc(100% + 20px)',
              top: '0',
            }}
          >
            <Text size="100" weight="medium" className="text-f1-foreground-inverse">
              {hoveringBuildingBlock?.title || ''}
            </Text>
            <Text size="100" className="text-f1-foreground-inverse">
              {hoveringBuildingBlock?.description || ''}
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
